import React, { useEffect } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Tooltip from 'components_new/atoms/Tooltip'

import { parseDateString } from 'helpers/Functions/Date'

import { LatestJobRun } from 'redux/reducers/DataPlatform/SIM'

interface ImportSectionProps {
  id: string
  latestRun: LatestJobRun | null | undefined
  onStart: (id: string) => void
  onPoll: (id: string) => void
  pollingIntervalId: ReturnType<typeof setInterval> | null
  setPollingIntervalId: (id: ReturnType<typeof setInterval> | null) => void
}

/**
 * Shared "Importera"-button + latest-run indicator, used by both the
 * custom and standard ontology pages to trigger/observe the ontology
 * job run. The caller supplies the entity id and its latest run, so this
 * component stays agnostic of where they are sourced from (v2 ontology or
 * v4 SIM).
 */
const ImportSection = ({
  id,
  latestRun,
  onStart,
  onPoll,
  pollingIntervalId,
  setPollingIntervalId
}: ImportSectionProps) => {
  const isRunning =
    latestRun?.state === 'RUNNING' || latestRun?.state === 'STARTING'

  useEffect(() => {
    if (isRunning) {
      if (!pollingIntervalId) {
        const intervalId = setInterval(() => {
          onPoll(id)
        }, 7000)

        setPollingIntervalId(intervalId)
      }
    } else {
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId)
        setPollingIntervalId(null)
      }
    }
  }, [latestRun])

  if (!latestRun) {
    return null
  }

  let title = ''
  let icon: React.ReactNode = undefined

  if (latestRun.state === 'SUCCEEDED') {
    title = latestRun.completed_on
      ? parseDateString(latestRun.completed_on)
      : ''
    icon = <Icon name="CheckCircleOutline" />
  } else if (latestRun.state === 'FAILED') {
    title = latestRun.completed_on
      ? parseDateString(latestRun.completed_on)
      : ''
    icon = <Icon name="WarningAmberOutlined" />
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button
        loading={isRunning}
        loadingTitle="Importerar"
        onClick={() => onStart(id)}
        variant="outlined"
        startIcon={<Icon name="RefreshOutlined" />}
      >
        Importera
      </Button>
      {!isRunning && (
        <Tooltip title="Senaste import av data">
          <Chip clickable icon={icon} label={title} size="small" />
        </Tooltip>
      )}
    </Box>
  )
}

export default ImportSection
